import { isDefined } from "~/shared/common";
import { TutorialName } from "~/shared/enums";
import type { Chat, ChatCreate, ChatMessage, ChatUpdate } from "~/shared/types";
import { isLexicalStateEmpty } from "~/utils/common";
import { chatComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToChats: Map<string, Chat>;
  _sortedChatDuids: string[];
};

export const state: State = {
  _duidsToChats: new Map(),
  _sortedChatDuids: [],
};

export type Getters = {
  chatList: Chat[];
  getChatByDuid: (duid: string) => Chat | undefined;
  getChatsByDuidsOrdered: (duids: string[]) => Chat[];
};

export type Actions = {
  isChatEmpty: (chat: Chat) => boolean;
  createChat: (chatCreate: ChatCreate, options?: { awaitBackend?: boolean }) => Promise<Chat | undefined>;
  updateChat: (
    update: ChatUpdate,
    options?: { awaitBackend?: boolean; noBackend?: boolean }
  ) => Promise<Chat | undefined>;
  addMessageToChat: (duid: string, message: ChatMessage) => void;
  deleteChat: (chat: Chat, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createChatNoBackend: (chatCreate: ChatCreate) => Chat | undefined;
  $setChats: (chats: Chat[]) => void;
  $createOrUpdateChat: (chat: Chat) => void;
  $deleteChat: (chat: Chat) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  chatList() {
    return this._sortedChatDuids.map((e) => this._duidsToChats.get(e)).filter(isDefined);
  },
  getChatByDuid() {
    return (duid) => this._duidsToChats.get(duid);
  },
  getChatsByDuidsOrdered() {
    return (duids) => this.chatList.filter((e) => duids.includes(e.duid));
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  isChatEmpty(chat) {
    return chat.messages.length === 0 && (chat.draft === null || isLexicalStateEmpty(chat.draft.content));
  },
  async createChat(chatCreate, options = {}) {
    const { awaitBackend = false } = options;
    const chat = this.$createChatNoBackend(chatCreate);
    if (!chat) {
      return undefined;
    }

    const backendAction = this.$backend.chat.create(chatCreate);
    if (awaitBackend) {
      await backendAction;
    }
    return chat;
  },
  async updateChat(update, options = {}) {
    const { awaitBackend = false, noBackend = false } = options;
    const chat = this.getChatByDuid(update.duid);
    if (!chat) {
      return undefined;
    }

    Object.assign(chat, update);
    updateInSorted(this._duidsToChats, this._sortedChatDuids, chat.duid, chatComparator);

    if (!noBackend) {
      const backendAction = this.$backend.chat.update(update);
      if (awaitBackend) {
        await backendAction;
      }
    }
    return chat;
  },
  addMessageToChat(duid, message) {
    const userStore = this.$useUserStore();
    const chat = this.getChatByDuid(duid);
    if (!chat) {
      return;
    }
    userStore.updateTutorialStatuses([{ name: TutorialName.BRAINSTORM_WITH_AI, status: 2 }]);
    chat.messages.push(message);
    chat.updatedAt = new Date().toISOString();
    updateInSorted(this._duidsToChats, this._sortedChatDuids, chat.duid, chatComparator);
  },
  async deleteChat(chat, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteChat(chat);

    const backendAction = this.$backend.chat.delete(chat.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $createChatNoBackend(chatCreate) {
    const now = new Date().toISOString();
    const chat: Chat = {
      ...chatCreate,
      updatedAt: now,
      userDuid: this.$useUserStore().duid,
      title: "",
      messages: [],
      extrasAreUserManaged: false,
    };

    this.$createOrUpdateChat(chat);

    return chat;
  },
  $setChats(chats) {
    setObjectMapAndSorted(this._duidsToChats, this._sortedChatDuids, chats, chatComparator);
  },
  $createOrUpdateChat(chat) {
    const currentChat = this.getChatByDuid(chat.duid);

    // TODO remove this check when we are filtering out spurious updates on BE of WS
    if (chat.userDuid !== this.$useUserStore().duid) {
      if (currentChat) {
        this.$deleteChat(chat);
      }
      return;
    }

    if (!currentChat) {
      this._duidsToChats.set(chat.duid, chat);
    } else {
      Object.assign(currentChat, chat);
    }
    updateInSorted(this._duidsToChats, this._sortedChatDuids, chat.duid, chatComparator);
  },
  $deleteChat(chat) {
    this._duidsToChats.delete(chat.duid);
    removeFromSorted(this._sortedChatDuids, chat.duid);
  },
};

export { actions, getters };
